import { useEffect, useState } from 'react';
import { Download, AlertTriangle, Loader2, ChevronRight } from 'lucide-react';
import { vatReportingApi } from '../../services/vatReportingApi';
import { useVatLang, useEcProblemText } from './VatLang';
import type { EcSalesResponse, IntrastatResponse, VatCompany } from '../../types/vatReporting';

/**
 * The two declarations that are filed as files rather than as a form.
 *
 * Both panels put the generated content on screen BEFORE offering the download. The manual
 * process produced a CSV out of a macro workbook that nobody could read before submitting it;
 * being able to see the eight lines that are about to go to the Finanzamt is most of the value.
 */

const money = (v: number, dp = 2) =>
    new Intl.NumberFormat('de-CH', { minimumFractionDigits: dp, maximumFractionDigits: dp }).format(v);
const whole = (v: number) => new Intl.NumberFormat('de-CH').format(v);

interface PanelProps {
    company: VatCompany;
    year: number;
    month: number;
    /**
     * Bumped by the tab whenever something changed the month's entry set - a manual exclusion,
     * a carry-forward. These two panels fetch their own endpoints, so without it they keep
     * showing the declaration as it was BEFORE the decision while the Kennzahlen above them
     * already moved. A figure and its file disagreeing is exactly what this page exists to
     * prevent; found in the Phase-5 live test, where the ZM still said "no intra-Community
     * supplies" for a month whose Kz 41 had just gained one.
     */
    reloadKey?: number;
    onError: (message: string) => void;
}

// ------------------------------------------------------------------ EC Sales ----

export function EcSalesPanel({ company, year, month, reloadKey, onError, onDrill }: PanelProps & {
    onDrill: (vatId: string, entryNos: number[]) => void;
}) {
    const { t } = useVatLang();
    const problemText = useEcProblemText();
    const [data, setData] = useState<EcSalesResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        vatReportingApi.getEcSales({ company, year, month })
            .then(r => !cancelled && setData(r))
            .catch(e => !cancelled && onError(e instanceof Error ? e.message : t('panel.loadFailedEc')))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [company, year, month, reloadKey, onError]);

    async function download() {
        setDownloading(true);
        try {
            await vatReportingApi.downloadEcSales({ company, year, month });
        } catch (e) {
            onError(e instanceof Error ? e.message : t('panel.downloadFailed'));
        } finally {
            setDownloading(false);
        }
    }

    return (
        <div className="border rounded-lg bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b bg-gray-50 flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-gray-900">{t('ec.title')}</h3>
                {data?.available && data.lines.length > 0 && (
                    <button onClick={download} disabled={downloading}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border rounded hover:bg-white disabled:opacity-50">
                        {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                        {t('ec.download')}
                    </button>
                )}
            </div>

            {loading && <div className="p-6 text-center text-gray-500 text-sm">{t('panel.loading')}</div>}

            {!loading && data && !data.available && (
                <div className="p-4 text-sm text-gray-600">{data.unavailableReason}</div>
            )}

            {!loading && data?.available && (
                <>
                    {data.lines.length === 0 && data.unassignable.length === 0 ? (
                        <div className="p-6 text-sm text-gray-500">
                            No intra-Community supplies in this period — no ZM to file.
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs uppercase text-gray-500 border-b">
                                    <th className="px-4 py-2 text-left">USt-IdNr.</th>
                                    <th className="px-4 py-2 text-left">Art</th>
                                    <th className="px-4 py-2 text-right">Betrag (EUR)</th>
                                    <th className="px-4 py-2 text-right">Posten</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.lines.map(l => (
                                    <tr key={`${l.vatRegistrationNo}|${l.ecSalesType}`}
                                        className={l.problems.length ? 'bg-red-50/60' : 'hover:bg-gray-50'}>
                                        <td className="px-4 py-1.5 font-mono">
                                            {l.vatRegistrationNo}
                                            {l.problems.length > 0 && (
                                                <span className="ml-1 inline-flex items-center text-red-600"
                                                      title={l.problems.map(problemText).join('; ')}>
                                                    <AlertTriangle className="h-3.5 w-3.5" />
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-1.5">{l.ecSalesType}</td>
                                        <td className="px-4 py-1.5 text-right tabular-nums">
                                            {whole(l.amount)}
                                            {Math.abs(l.rawAmount - l.amount) >= 0.005 && (
                                                <span className="ml-1 text-xs text-gray-400"
                                                      title={`Each line is rounded up: ${money(l.rawAmount)}`}>↑</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-1.5 text-right">
                                            <button onClick={() => onDrill(l.vatRegistrationNo, l.entryNos)}
                                                    className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 tabular-nums">
                                                {l.entryCount}<ChevronRight className="h-3.5 w-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-50 border-t font-medium">
                                <tr>
                                    <td className="px-4 py-2" colSpan={2}>{t('ec.controlTotal')}</td>
                                    <td className="px-4 py-2 text-right tabular-nums">{whole(data.controlTotal)}</td>
                                    <td />
                                </tr>
                            </tfoot>
                        </table>
                    )}

                    {data.unassignable.length > 0 && (
                        <div className="px-4 py-2 border-t bg-red-50 text-sm text-red-800">
                            <strong>{data.unassignable.length} exempt suppl{data.unassignable.length === 1 ? 'y' : 'ies'}</strong>{' '}
                            carry no customer VAT number and cannot go into the ZM:{' '}
                            {data.unassignable.slice(0, 3).map(u => u.documentNo).join(', ')}
                            {data.unassignable.length > 3 && ` and ${data.unassignable.length - 3} more`}.
                        </div>
                    )}

                    {!data.reconciled && data.unreconciledReason && (
                        <div className="px-4 py-2 border-t bg-amber-50 text-xs text-amber-900">
                            <strong>{t('panel.notReconciled')}</strong> {t('panel.notReconciledHint')}
                        </div>
                    )}
                    {data.kz41 !== null && (
                        <div className="px-4 py-2 border-t bg-gray-50 text-xs text-gray-600">
                            {t('ec.roundingNote', {
                                kz41: whole(data.kz41),
                                controlTotal: whole(data.controlTotal),
                                tolerance: data.roundingTolerance,
                                lines: data.lines.length,
                            })}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// ------------------------------------------------------------------ Intrastat ----

export function IntrastatPanel({ company, year, month, reloadKey, onError }: PanelProps) {
    const { t } = useVatLang();
    const [data, setData] = useState<IntrastatResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState<'csv' | 'review' | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        vatReportingApi.getIntrastat({ company, year, month })
            .then(r => !cancelled && setData(r))
            .catch(e => !cancelled && onError(e instanceof Error ? e.message : t('panel.loadFailedIntrastat')))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [company, year, month, reloadKey, onError]);

    async function download(kind: 'csv' | 'review') {
        setDownloading(kind);
        try {
            if (kind === 'csv') await vatReportingApi.downloadIntrastat({ company, year, month });
            else await vatReportingApi.downloadIntrastatReview({ company, year, month });
        } catch (e) {
            onError(e instanceof Error ? e.message : t('panel.downloadFailed'));
        } finally {
            setDownloading(null);
        }
    }

    const hasLines = !!data?.available && data.lines.length > 0;

    return (
        <div className="border rounded-lg bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b bg-gray-50 flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-gray-900">Intrastat</h3>
                {hasLines && (
                    <div className="flex items-center gap-1.5">
                        <button onClick={() => download('review')} disabled={downloading !== null}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border rounded hover:bg-white disabled:opacity-50">
                            {downloading === 'review' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                            {t('intrastat.review')}
                        </button>
                        <button onClick={() => download('csv')} disabled={downloading !== null}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border rounded hover:bg-white disabled:opacity-50">
                            {downloading === 'csv' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                            {t('intrastat.download')}
                        </button>
                    </div>
                )}
            </div>

            {/* Excel strips the leading zero from "09"/"08" on save and IDEV then refuses the
                import - reported by Heike on 2026-09-16 and again for September. */}
            {hasLines && (
                <div className="px-4 py-2 border-b bg-amber-50 text-xs text-amber-900 flex items-start gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>{t('intrastat.uploadHint')}</span>
                </div>
            )}

            {loading && <div className="p-6 text-center text-gray-500 text-sm">{t('panel.loading')}</div>}

            {!loading && data && !data.available && (
                <div className="p-4 text-sm text-gray-600">{data.unavailableReason}</div>
            )}

            {!loading && data?.available && (
                <>
                    {data.lines.length === 0 ? (
                        <div className="p-6 text-sm text-gray-500">{t('intrastat.noMovements')}</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-xs uppercase text-gray-500 border-b">
                                        <th className="px-3 py-2 text-left">Ri.</th>
                                        <th className="px-3 py-2 text-left">Partner</th>
                                        <th className="px-3 py-2 text-left">Urspr.</th>
                                        <th className="px-3 py-2 text-left">Warennr.</th>
                                        <th className="px-3 py-2 text-left">Warenbezeichnung</th>
                                        <th className="px-3 py-2 text-right">kg</th>
                                        <th className="px-3 py-2 text-right">EUR</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {data.lines.map(l => (
                                        <tr key={l.entryNo} className={l.problems.length ? 'bg-amber-50/60' : 'hover:bg-gray-50'}>
                                            <td className="px-3 py-1.5">{l.direction}</td>
                                            <td className="px-3 py-1.5">{l.partnerCountry ?? '—'}</td>
                                            <td className="px-3 py-1.5">{l.originCountry ?? '—'}</td>
                                            <td className="px-3 py-1.5 font-mono">{l.tariffNo}</td>
                                            <td className="px-3 py-1.5 truncate max-w-[16rem]" title={l.description}>
                                                {l.description}
                                                {l.problems.length > 0 && (
                                                    <span className="ml-1 inline-flex items-center text-amber-700"
                                                          title={l.problems.join('; ')}>
                                                        <AlertTriangle className="h-3.5 w-3.5" />
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-1.5 text-right tabular-nums">{whole(l.netMass)}</td>
                                            <td className="px-3 py-1.5 text-right tabular-nums">{whole(l.invoiceValue)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-gray-50 border-t font-medium">
                                    <tr>
                                        <td className="px-3 py-2" colSpan={5}>
                                            Versendung {whole(data.dispatchTotal)} · Eingang {whole(data.arrivalTotal)}
                                        </td>
                                        <td />
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            {whole(data.dispatchTotal + data.arrivalTotal)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}

                    {data.missingTariff.length > 0 && (
                        <div className="px-4 py-2 border-t bg-red-50 text-sm text-red-800">
                            <strong>{t('intrastat.missingTariff', { count: data.missingTariff.length })}</strong>{' '}
                            {data.missingTariff.slice(0, 2).map(m =>
                                `${m.itemNo ?? '?'} ${m.description} (${m.quantity} ${m.unitOfMeasure ?? ''})`).join('; ')}
                            {data.missingTariff.length > 2
                                && ` ${t('intrastat.andMore', { count: data.missingTariff.length - 2 })}`}.
                            {' '}{t('intrastat.missingTariffHint')}
                        </div>
                    )}

                    {!data.reconciled && data.unreconciledReason && (
                        <div className="px-4 py-2 border-t bg-amber-50 text-xs text-amber-900">
                            <strong>{t('panel.notReconciled')}</strong> {t('panel.notReconciledHint')}
                        </div>
                    )}
                    <div className="px-4 py-2 border-t bg-gray-50 text-xs text-gray-600">
                        {t('intrastat.excludedCharges', { count: data.excludedCharges.length })}
                        {data.zeroLines.length > 0
                            && `; ${t('intrastat.zeroLines', { count: data.zeroLines.length })}`}
                        {data.cancelledReversals.length > 0
                            && `; ${t('intrastat.cancelled', { count: data.cancelledReversals.length })}`}.
                        {data.constants && (
                            <> Art des Geschäfts {data.constants.natureOfTransaction},
                            Verkehrszweig {data.constants.modeOfTransport}.</>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
